import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';

import { BroadcastService } from '@app/services/broadcast.service';

import { UserInfoService } from '../../../../services/user-info.service';
import { ZegoEngineService } from '../../../../services/zego-engine.service';
import { ZegoMediaControlService } from '../../../../services/zego-media-control.service';
import { isDevice, isSafari } from '../../../../utils/utils';
import { CALL_STATE, CALL_TYPE } from '../../constants';
import { CallStoreService } from '../../services/call-store.service';
import { CallZegoService } from '../../services/call-zego.service';
import { PipService } from '../../services/pip.service';
import { CallActionBarComponent } from '../call-action-bar/call-action-bar.component';
import { CallAudioLayoutComponent } from '../call-audio-layout/call-audio-layout.component';
import { CallConfirmModalComponent } from '../call-confirm-modal/call-confirm-modal.component';
import { CallVideoLayoutComponent } from '../call-video-layout/call-video-layout.component';

@Component({
  selector: 'app-call-active',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CallActionBarComponent,
    CallVideoLayoutComponent,
    CallAudioLayoutComponent,
    CallConfirmModalComponent,
  ],
  templateUrl: './call-active.component.html',
  styleUrl: './call-active.component.scss',
})
export class CallActiveComponent implements OnDestroy, OnInit, AfterViewInit {
  readonly isFullscreen = input<boolean>(false);

  readonly callStore = inject(CallStoreService);
  private readonly callZegoService = inject(CallZegoService);
  public readonly userInfoService = inject(UserInfoService);
  private readonly zegoEngineService = inject(ZegoEngineService);
  private readonly zegoMediaControlService = inject(ZegoMediaControlService);
  private readonly toastr = inject(ToastrService);
  private readonly pipService = inject(PipService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly broadcastService = inject(BroadcastService);

  closeModalEvt = output();

  // Expose store signals
  readonly currentCall = this.callStore.currentCall;
  readonly callType = this.callStore.callType;
  readonly callState = this.callStore.callState;
  readonly participants = this.callStore.participants;
  readonly isInCall = this.callStore.isInCall;
  readonly activeParticipants = this.callStore.activeParticipants;

  readonly isShowMyNetworkWarning = this.callStore.isShowMyNetworkWarning;
  readonly isShowMyNetworkReconnecting = this.callStore.isShowMyNetworkReconnecting;
  readonly isShowRemoteNetworkWarning = this.callStore.isShowRemoteNetworkWarning;
  readonly isInviter = this.callStore.isInviter;
  readonly localStream = this.callStore.localStream;
  readonly localStreamId = this.callStore.localStreamId;
  readonly localMicState = this.callStore.localMicState;
  readonly localCamState = this.callStore.localCamState;
  readonly localSoundLevel = this.callStore.localSoundLevel;

  readonly localMediaPermissionService = this.callStore.localMediaPermissionService;
  readonly isCamPermissionGranted = this.callStore.isCamPermissionGranted;
  readonly isMicPermissionGranted = this.callStore.isMicPermissionGranted;
  readonly hasCamDevice = this.callStore.hasCamDevice;
  readonly hasMicDevice = this.callStore.hasMicDevice;
  readonly isScreenSharing = this.callStore.isScreenSharing;
  readonly isAnyScreenSharing = this.callStore.isAnyScreenSharing;
  readonly isOpenPip = this.pipService.isOpen;

  currentIsMicPermission = signal<boolean>(true);
  currentIsCamPermission = signal<boolean>(true);
  currentHasMicDevice = signal<boolean>(true);
  currentHasCamDevice = signal<boolean>(true);

  showSwitchCallModal = signal<boolean>(false);
  showConfirmShareScreenModal = signal<boolean>(false);

  showVideoLayout = computed(
    () =>
      (this.isInCall() &&
        this.callType() === CALL_TYPE.VIDEO &&
        this.callState() === CALL_STATE.CONNECTED) ||
      this.isAnyScreenSharing(),
  );
  isDevice = computed(() => isDevice(this.participants()[0]?.userId));

  constructor() {
    effect(
      () => {
        const permission = this.isMicPermissionGranted();
        if (this.currentIsMicPermission() === permission) return;
        this.currentIsMicPermission.set(permission);

        if (!permission && this.localStream() && this.localStreamId()) {
          if (
            this.callType() === CALL_TYPE.AUDIO ||
            (this.localStream()!.getAudioTracks().length > 0 &&
              this.localStream()!.getVideoTracks().length === 0)
          ) {
            this.callZegoService.stopPubishAndDestroyStream(
              this.localStream(),
              this.localStreamId()!,
            );
            this.zegoMediaControlService.setMediaState(false, false);
          } else if (
            this.localStream()!.getAudioTracks().length > 0 &&
            this.localStream()!.getVideoTracks().length > 0
          ) {
            this.callZegoService.stopPubishAndDestroyStream(
              this.localStream(),
              this.localStreamId()!,
            );
            const currentCall = this.currentCall();
            if (currentCall) {
              this.callStore.publishCallStream(currentCall.callId, currentCall.type);
            }
          }
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const permission = this.isCamPermissionGranted();
        if (this.currentIsCamPermission() === permission) return;
        this.currentIsCamPermission.set(permission);

        if (!permission && this.localStream() && this.localStreamId()) {
          if (
            this.localStream()!.getVideoTracks().length > 0 &&
            this.localStream()!.getAudioTracks().length === 0
          ) {
            this.callZegoService.stopPubishAndDestroyStream(
              this.localStream(),
              this.localStreamId()!,
            );
            this.zegoMediaControlService.setMediaState(false, false);
          } else if (
            this.localStream()!.getAudioTracks().length > 0 &&
            this.localStream()!.getVideoTracks().length > 0
          ) {
            this.callZegoService.stopPubishAndDestroyStream(
              this.localStream(),
              this.localStreamId()!,
            );
            const currentCall = this.currentCall();
            if (currentCall) {
              this.callStore.publishCallStream(currentCall.callId, currentCall.type);
            }
          }
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const hasCam = !!this.hasCamDevice();
        const hasMic = !!this.hasMicDevice();

        const prevCam = this.currentHasCamDevice();
        const prevMic = this.currentHasMicDevice();

        if (hasCam === prevCam && hasMic === prevMic) return;

        this.currentHasCamDevice.set(hasCam);
        this.currentHasMicDevice.set(hasMic);

        if (!this.localStream() || !this.localStreamId()) return;

        const hasVideoTrack = this.localStream()!.getVideoTracks().length > 0;
        const hasAudioTrack = this.localStream()!.getAudioTracks().length > 0;

        if (!hasCam && !hasMic) {
          this.callZegoService.stopPubishAndDestroyStream(
            this.localStream(),
            this.localStreamId()!,
          );
          this.zegoMediaControlService.setMediaState(false, false);
          return;
        }

        if (hasVideoTrack && hasAudioTrack) {
          this.callZegoService.stopPubishAndDestroyStream(
            this.localStream(),
            this.localStreamId()!,
          );
          const currentCall = this.currentCall();
          if (currentCall) {
            this.callStore.publishCallStream(currentCall.callId, currentCall.type);
          }
          return;
        }

        if ((hasVideoTrack && !hasCam) || (hasAudioTrack && !hasMic)) {
          this.callZegoService.stopPubishAndDestroyStream(
            this.localStream(),
            this.localStreamId()!,
          );
          this.zegoMediaControlService.setMediaState(false, false);
        }
      },
      { allowSignalWrites: true },
    );
  }

  async ngOnInit(): Promise<void> {
    if (isSafari()) {
      document.addEventListener('click', this.handleUserClick, { capture: true, passive: true });
    }

    this.callZegoService.setupStreamCallbacks();

    this.broadcastService.meeting$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      if (res.action === 'start-meeting') {
        this.callStore.endCurrentCall();
      }
    });
  }

  ngAfterViewInit(): void {
    if (!isSafari()) return;
    if (!this.localMicState()) {
      this.requestCamMicPermission('mic');
    }
    if (!this.localCamState() && this.callType() === CALL_TYPE.VIDEO && this.hasCamDevice()) {
      this.requestCamMicPermission('cam');
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.handleUserClick);
    this.callStore.destroyCallZego();
  }

  async requestCamMicPermission(type: 'cam' | 'mic') {
    if (type === 'mic') {
      const isGranted = await this.localMediaPermissionService.requestCameraMicPermission('mic');
      if (isGranted) {
        this.currentIsMicPermission.set(true);
      }
    }
    if (type === 'cam') {
      const isGranted = await this.localMediaPermissionService.requestCameraMicPermission('cam');
      if (isGranted) {
        this.currentIsCamPermission.set(true);
      }
    }
  }

  handleClose() {
    this.closeModalEvt.emit();
  }

  handleUserClick = (): void => {
    this.zegoEngineService.unlockAudioFromUserInteraction();
  };

  async handleSwitchCallType() {
    this.callStore.switchCallType();
  }

  async handleToggleCam() {
    this.showSwitchCallModal.set(false);
    if (this.callType() === CALL_TYPE.AUDIO) {
      this.showSwitchCallModal.set(true);
      return;
    }
    this.executeToggleCam();
  }

  executeToggleCam() {
    if (this.localStream()) {
      const videoTrack = this.localStream()?.getVideoTracks()[0];
      const hasCamera = !!videoTrack;
      if (!hasCamera) {
        this.callZegoService.stopPubishAndDestroyStream(this.localStream(), this.localStreamId()!);
        const currentCall = this.currentCall();
        if (currentCall) {
          this.callStore.publishCallStream(currentCall.callId, CALL_TYPE.VIDEO);
        }
        return;
      }
      this.zegoMediaControlService.toggleCamera(this.localStream()!);
    } else {
      const currentCall = this.currentCall();
      if (currentCall) {
        this.callStore.publishCallStream(currentCall.callId, CALL_TYPE.VIDEO);
      }
    }
  }

  handleToggleShareScreen() {
    if (this.callZegoService.isRemoteSharing()) {
      this.showConfirmShareScreenModal.set(true);
      return;
    }
    this.toggleScreenShare();
  }

  async toggleScreenShare(): Promise<void> {
    const beforeState = this.isScreenSharing();
    let screenPreviewElement = document.getElementById('screen-preview') as HTMLDivElement;
    if (window.documentPictureInPicture?.window) {
      screenPreviewElement = window.documentPictureInPicture.window.document.getElementById(
        'screen-preview',
      ) as HTMLDivElement;
    }
    const success = await this.zegoMediaControlService.toggleScreenShare(
      this.callStore.zimService.userId,
      screenPreviewElement,
    );

    if (!success && !this.isScreenSharing()) {
      this.toastr.error('Không thể chia sẻ màn hình. Vui lòng thử lại.');
    }
    if (success && this.isScreenSharing() !== beforeState) {
      this.toastr.info(
        this.isScreenSharing() ? 'Bạn vừa chia sẻ màn hình.' : 'Bạn đã dừng chia sẻ màn hình.',
      );
    }
  }
}
